import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, ChevronUp, ChevronDown, Disc3 } from "lucide-react";
import { useApp } from "../contexts/AppContext";

// Defaults are kept in code so the page works before the admin saves anything.
const DEFAULT_TRACK = {
  title: "İntizar (Vəfadarım Mənim)",
  artist: "Elmira Rəhimova",
  src: "https://customer-assets.emergentagent.com/job_font-harmony-mobile/artifacts/15epylgz_Elmira%20R%C9%99himova%20-%20%C4%B0ntizar%20%28V%C9%99fadar%C4%B1m%20M%C9%99nim%29.mp3",
};

const STORAGE_KEY = "vbaz_ambient_v1";
const ANIM_MS = 320;

function pickLang(v, lang) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return v[lang] || v.az || v.en || Object.values(v)[0] || "";
}

export default function AmbientPlayer() {
  const { content, lang } = useApp() || {};
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);     // start muted so autoplay isn't blocked
  const [collapsed, setCollapsed] = useState(false);
  const [animating, setAnimating] = useState(false); // brief flag for open/close transition
  const [progress, setProgress] = useState(0);  // 0..100
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Resolve which track to play — prefer admin-configured one, fall back to default.
  const ap = content?.ambient_player || {};
  const track = {
    title: pickLang(ap.title, lang) || DEFAULT_TRACK.title,
    artist: pickLang(ap.artist, lang) || DEFAULT_TRACK.artist,
    src: ap.src || DEFAULT_TRACK.src,
  };

  // Restore previous state
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && typeof saved === "object") {
        if (typeof saved.collapsed === "boolean") setCollapsed(saved.collapsed);
      }
    } catch (_) { /* ignore */ }
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ collapsed }));
  }, [collapsed]);

  // Try to autoplay (muted) — most browsers allow muted autoplay.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.6;
    a.muted = true;
    const tryPlay = async () => {
      try {
        await a.play();
        setPlaying(true);
      } catch (_e) {
        setPlaying(false);
      }
    };
    tryPlay();
  }, [track.src]);

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      try {
        a.muted = false;
        setMuted(false);
        await a.play();
        setPlaying(true);
      } catch (_e) { /* ignore */ }
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    setMuted(a.muted);
  };

  const handleCollapse = () => {
    setAnimating(true);
    setTimeout(() => {
      setCollapsed(true);
      setAnimating(false);
    }, ANIM_MS);
  };

  const handleExpand = () => {
    setAnimating(true);
    setCollapsed(false);
    setTimeout(() => setAnimating(false), ANIM_MS);
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a) return;
    setCurrentTime(a.currentTime || 0);
    if (a.duration) {
      setDuration(a.duration);
      setProgress((a.currentTime / a.duration) * 100);
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = a.duration * pct;
  };

  const fmt = (s) => {
    if (!Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (!track.src) return null;

  // collapsing animation classes for the expanded bar
  const barAnimClass = collapsed
    ? "" // never used here
    : animating
      ? "ambient-bar-exit"   // shrinking towards the left puck
      : "ambient-bar-enter"; // expanding from puck

  return (
    <>
      <audio
        ref={audioRef}
        src={track.src}
        loop
        preload="metadata"
        onTimeUpdate={onTime}
        onLoadedMetadata={onTime}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      <style>{`
        @keyframes ambientBarEnter {
          0%   { transform: translateX(-12px) scale(0.6); opacity: 0; filter: blur(2px); }
          100% { transform: translateX(0) scale(1); opacity: 1; filter: blur(0); }
        }
        @keyframes ambientBarExit {
          0%   { transform: translateX(0) scale(1); opacity: 1; filter: blur(0); }
          100% { transform: translateX(-12px) scale(0.55); opacity: 0; filter: blur(2px); }
        }
        @keyframes ambientPuckIn {
          0%   { transform: scale(0.4); opacity: 0; }
          60%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .ambient-bar-enter { animation: ambientBarEnter ${ANIM_MS}ms cubic-bezier(.2,.7,.2,1) both; transform-origin: left center; }
        .ambient-bar-exit  { animation: ambientBarExit  ${ANIM_MS}ms cubic-bezier(.4,0,.6,1) both; transform-origin: left center; pointer-events: none; }
        .ambient-puck-in   { animation: ambientPuckIn   ${ANIM_MS}ms cubic-bezier(.2,.8,.2,1) both; }
        @media (min-width: 640px) {
          .ambient-bar-enter, .ambient-bar-exit { transform-origin: center bottom; }
          @keyframes ambientBarEnter {
            0%   { transform: translateX(-50%) scale(0.7); opacity: 0; }
            100% { transform: translateX(-50%) scale(1); opacity: 1; }
          }
          @keyframes ambientBarExit {
            0%   { transform: translateX(-50%) scale(1); opacity: 1; }
            100% { transform: translateX(-50%) scale(0.7); opacity: 0; }
          }
        }
      `}</style>

      {collapsed ? (
        /* COLLAPSED — same-size vinyl puck (matches AIChat 56x56), bottom-left on mobile, bottom-center on desktop. */
        <>
          {/* Mobile: bottom-left small vinyl */}
          <button
            onClick={handleExpand}
            className="md:hidden fixed left-5 bottom-5 z-[55] w-14 h-14 rounded-full bg-black/85 text-white backdrop-blur-md border border-white/15 shadow-2xl flex items-center justify-center hover:bg-black active:scale-95 transition-all ambient-puck-in"
            aria-label="Open player"
            data-testid="ambient-expand-mobile"
          >
            <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--accent)] via-zinc-700 to-black flex items-center justify-center relative overflow-hidden shadow-inner">
              <Disc3 size={18} className={playing ? "vinyl-spin" : ""} />
              <span className="absolute w-1.5 h-1.5 rounded-full bg-white/80" />
            </span>
          </button>
          {/* Desktop: bottom-center pill */}
          <div
            className="hidden md:block fixed left-1/2 -translate-x-1/2 bottom-5 z-[55]"
            data-testid="ambient-player"
          >
            <button
              onClick={handleExpand}
              className="group flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-full bg-black/85 text-white backdrop-blur-md border border-white/10 shadow-2xl hover:bg-black transition-colors ambient-puck-in"
              aria-label="Open player"
              data-testid="ambient-expand"
            >
              <span className="w-8 h-8 rounded-full bg-[var(--accent)] flex items-center justify-center">
                <Disc3 size={16} className={playing ? "vinyl-spin" : ""} />
              </span>
              <span className="text-[11px] tracking-[0.18em] uppercase font-light">
                {playing ? "Çalır" : "Pauza"}
              </span>
              <ChevronUp size={14} className="opacity-70 group-hover:opacity-100" />
            </button>
          </div>
        </>
      ) : (
        /* EXPANDED — compact pill. Mobile: left aligned (room reserved for AIChat). */
        <div
          className={`fixed left-5 right-[88px] sm:left-1/2 sm:right-auto sm:-translate-x-1/2 bottom-5 z-[55] ${barAnimClass}`}
          data-testid="ambient-player"
        >
          <div
            className="flex items-center gap-2 sm:gap-3 pl-1.5 pr-2 sm:pr-3 py-1.5 rounded-full bg-black/85 text-white backdrop-blur-md border border-white/10 shadow-2xl w-full sm:w-auto sm:min-w-[320px] sm:max-w-[460px]"
          >
            {/* Vinyl cover */}
            <button
              onClick={togglePlay}
              className="relative w-11 h-11 rounded-full bg-gradient-to-br from-[var(--accent)] via-zinc-700 to-black flex items-center justify-center overflow-hidden shrink-0 group"
              aria-label={playing ? "Pause" : "Play"}
              data-testid="ambient-play-toggle"
            >
              <Disc3 size={20} className={playing ? "vinyl-spin opacity-90" : "opacity-90"} />
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
              </span>
            </button>

            {/* Title + progress */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="text-[12px] sm:text-[12.5px] font-medium truncate">{track.title}</span>
                <span className="text-[11px] text-white/55 truncate hidden sm:inline">— {track.artist}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-white/55 font-mono w-8 text-right hidden sm:inline">{fmt(currentTime)}</span>
                <div
                  className="flex-1 h-1 bg-white/15 rounded-full cursor-pointer relative group/track"
                  onClick={seek}
                  data-testid="ambient-progress"
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-white rounded-full group-hover/track:bg-[var(--accent)] transition-colors"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-[10px] text-white/55 font-mono w-8 hidden sm:inline">{fmt(duration)}</span>
              </div>
            </div>

            {/* Controls */}
            <button
              onClick={toggleMute}
              className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-white/70 hover:text-white shrink-0"
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="ambient-mute"
            >
              {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            </button>
            <button
              onClick={handleCollapse}
              className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-white/70 hover:text-white shrink-0"
              aria-label="Collapse"
              data-testid="ambient-collapse"
            >
              <ChevronDown size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
